import { BxfButton } from '@blk/bxf-web-components/dist/components/bxf-button';
import { MouseEvent, useReducer } from 'react';
import { ChatBlkTelemetryService } from '../services';
import { ChatBlkQuery } from '../types';
import useChatBlkStore from './useChatBlkStore';

enum FeedbackActionTypeEnum {
    SET_RATING = 'SET_RATING',
    SET_CHECKBOX_ANSWERS = 'SET_CHECKBOX_ANSWERS',
    SET_COMMENT = 'SET_COMMENT',
    SET_REWRITE = 'SET_REWRITE',
    SET_SHOULD_DISPLAY_ADDITIONAL_FEEDBACK = 'SET_SHOULD_DISPLAY_ADDITIONAL_FEEDBACK',
}

type FeedbackAction =
    | {
          type: FeedbackActionTypeEnum.SET_RATING;
          payload: { value: string; rating: number };
      }
    | { type: FeedbackActionTypeEnum.SET_CHECKBOX_ANSWERS; payload: string[] }
    | { type: FeedbackActionTypeEnum.SET_COMMENT; payload: string }
    | { type: FeedbackActionTypeEnum.SET_REWRITE; payload: string }
    | {
          type: FeedbackActionTypeEnum.SET_SHOULD_DISPLAY_ADDITIONAL_FEEDBACK;
          payload: boolean;
      };

type FeedbackState = {
    rating?: { value: string; rating: number };
    checkboxAnswers: string[];
    comment: string;
    rewrite: string;
    shouldDisplayAdditionalFeedback: boolean;
};

function feedbackReducer(
    state: FeedbackState,
    action: FeedbackAction
): FeedbackState {
    switch (action.type) {
        case FeedbackActionTypeEnum.SET_RATING:
            return { ...state, rating: action.payload };
        case FeedbackActionTypeEnum.SET_CHECKBOX_ANSWERS:
            return { ...state, checkboxAnswers: action.payload };
        case FeedbackActionTypeEnum.SET_COMMENT:
            return { ...state, comment: action.payload };
        case FeedbackActionTypeEnum.SET_REWRITE:
            return { ...state, rewrite: action.payload };
        case FeedbackActionTypeEnum.SET_SHOULD_DISPLAY_ADDITIONAL_FEEDBACK:
            return {
                ...state,
                shouldDisplayAdditionalFeedback: action.payload,
            };
        default:
            return state;
    }
}

const initialState: FeedbackState = {
    rating: { value: '', rating: 0 },
    checkboxAnswers: [],
    comment: '',
    rewrite: '',
    shouldDisplayAdditionalFeedback: false,
};

const useFeedback = () => {
    const [state, dispatch] = useReducer(feedbackReducer, initialState);
    const { activeQueryId, updateQuery, setIsRequiredFeedbackSubmitted } =
        useChatBlkStore();

    const handleSubmitFeedback = (event: MouseEvent<BxfButton, MouseEvent>) => {
        event.stopPropagation();
        const updatedProps = {
            feedbackComment: state.comment,
            answerRating: state.rating?.rating,
            feedbackCategories: state.checkboxAnswers,
            feedbackRewrite: state.rewrite,
        } as ChatBlkQuery;
        updateQuery({
            hashId: activeQueryId,
            updatedProps,
        });
        ChatBlkTelemetryService.trackFeedback(updatedProps);
        setIsRequiredFeedbackSubmitted(true);
    };

    return {
        shouldDisplayAdditionalFeedback: state.shouldDisplayAdditionalFeedback,
        isSubmitDisabled:
            state.rating?.rating < 4 && state.checkboxAnswers.length === 0,
        handleSubmitFeedback,
        setRating: (value: string, rating: number) => {
            dispatch({
                type: FeedbackActionTypeEnum.SET_RATING,
                payload: { value, rating },
            });
            dispatch({
                type: FeedbackActionTypeEnum.SET_SHOULD_DISPLAY_ADDITIONAL_FEEDBACK,
                payload: rating < 4,
            });
        },
        setCheckboxAnswers: (label: string, checked: boolean) => {
            const answers = new Set(state.checkboxAnswers);
            if (checked) {
                answers.add(label);
            } else {
                answers.delete(label);
            }
            dispatch({
                type: FeedbackActionTypeEnum.SET_CHECKBOX_ANSWERS,
                payload: Array.from(answers),
            });
        },
        setComment: (comment: string) =>
            dispatch({
                type: FeedbackActionTypeEnum.SET_COMMENT,
                payload: comment,
            }),
        setRewrite: (rewrite: string) =>
            dispatch({
                type: FeedbackActionTypeEnum.SET_REWRITE,
                payload: rewrite,
            }),
    };
};

export default useFeedback;
